/*
 * Translation engine — the single client-side path for Japanese → English
 * display text.
 *
 * Japanese is canonical: this module never mutates application state or data,
 * it only asks the Sensbey backend (`POST /api/translate/batch`, which routes to
 * the configured LOCAL LLM) for a derived English view and caches the result.
 *
 * Responsibilities
 *   · normalised keying + in-memory cache (no duplicate requests for the same
 *     source text)
 *   · in-flight reuse + debounced batching (max 30 items, max 2 concurrent)
 *   · epoch guard so a late English response is discarded after the user
 *     switched back to Japanese (or navigated away)
 *   · circuit breaker + degraded status so a failing model never loops
 *   · skip rules (empty / numeric / punctuation / ASCII-only / identifiers)
 */
import axios from 'axios';
import { staticTranslate } from './staticPatterns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/translate`;

const BATCH_SIZE = 8;          // iter_293: 小さく刻む（1件=1回の平文翻訳）
const DEBOUNCE_MS = 120;
const MAX_CONCURRENT = 2;
const MAX_ITEM_CHARS = 2000;
const FAILURE_PAUSE_MS = 60000;
const NEGATIVE_TTL_MS = 5 * 60 * 1000;

const JP_RE = /[\u3040-\u30ff\u4e00-\u9fff]/;
const NUMERIC_RE = /^[\d\s.,:/%+\-()（）]+$/;
const PUNCT_RE = /^[^\p{L}\p{N}]+$/u;
const PROTECTED_RE = /^(https?:\/\/|\/|\\\\|[A-Za-z]:\\)|^[\w.\-/\\()（）]+\.(pdf|docx?|xlsx?|pptx?|txt|md|csv|png|jpe?g|gif|webp|zip|dwg)$|^[A-Z0-9][A-Z0-9_\-./]{2,}$/i;

const cache = new Map();      // key -> english string
const negative = new Map();   // key -> timestamp of last failure/skip
const inflight = new Map();   // key -> array of resolvers
// T1 — 検証で落とした（＝壊れた訳を捨てた）文字列。管理者向けの印だけに使う。
// モデルに繋がらない等の転送障害は含めない（それは状態表示の役目）。
const rejected = new Set();
const REJECT_REASONS = new Set([
  'markdown_table', 'glossary_dump', 'multiline_for_single_line',
  'output_too_long', 'identifier_dropped', 'markdown_decoration',
  'source_echo', 'explanatory_output', 'protected_identifier_changed',
  'locked_glossary_changed', 'malformed_output', 'empty_response',
  'reasoning_only', 'thinking_only', 'thinking_unclosed', 'truncated',
]);
export const isRejected = (text) => rejected.has(normaliseKey(text));

const STORE_KEY = 'sensbey_tr_cache_v1';
const STORE_LIMIT = 1500;
let cacheVersion = null;
let saveTimer = null;

/** Persisted cache — makes EN instant on revisits. Dropped automatically when
 *  the prompt version or the admin glossary changes (cache_version). */
function loadPersisted() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return;
    cacheVersion = parsed.version || null;
    Object.entries(parsed.entries || {}).forEach(([k, v]) => {
      if (typeof v === 'string' && v) cache.set(k, v);
    });
  } catch { /* ignore corrupt storage */ }
}
function persist() {
  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    try {
      const entries = {};
      let n = 0;
      for (const [k, v] of cache) {
        if (n >= STORE_LIMIT) break;
        entries[k] = v; n += 1;
      }
      localStorage.setItem(STORE_KEY, JSON.stringify({ version: cacheVersion, entries }));
    } catch { /* quota — memory cache still works */ }
  }, 1500);
}
function dropPersisted(version) {
  cache.clear();
  cacheVersion = version;
  try { localStorage.removeItem(STORE_KEY); } catch { /* noop */ }
}
loadPersisted();

let queue = [];               // [{ key, text, contentType, epoch }]
let timer = null;
let active = 0;
let epoch = 0;
let degraded = false;
let pausedUntil = 0;
let consecutiveFailures = 0;
let lastErrorCategory = null;
const listeners = new Set();
// 先読み（プリウォーム）は既定で停止。サーバーが明示的に許可した場合のみ動く。
// （2026-06 転送障害の封じ込め: 失敗し続ける先読みが共有モデルを占有していた）
let prewarmAllowed = false;
let prewarmSuspendedReason = null;

export const normaliseKey = (text) => String(text || '').replace(/\s+/g, ' ').trim();

// ── T2: 業務データ（人名・会社名）は翻訳機にかけない ────────────────────
// 本番で 海老原製作所→"Kobayashi Seisakusho"、三木 和美→"Tanimura Mami" の
// ように別人・別社名になっていた。名簿と取引先名を取得して、その語を含む文は
// 訳さない（英語名 name_en が入っているものは表示だけ差し替える）。
let protectedNames = [];
let nameEnMap = {};

export function setProtectedNames(terms, nameEn) {
  protectedNames = (terms || []).filter((t) => t && t.length >= 2);
  nameEnMap = nameEn || {};
}

export const getNameEn = (text) => nameEnMap[String(text || '').trim()] || null;

export function containsProtectedName(text) {
  const s = String(text || '');
  return protectedNames.some((n) => s.includes(n));
}

// ── T2 安全網: 業務データらしい文字列は名簿に無くてもモデルに送らない ──────
// 画面側で data-no-translate を付け忘れても、形で分かるものはここで止める。
const PERSON_NAME_RE =
  /^[\u4e00-\u9fff々]{1,4}[ \u3000][\u4e00-\u9fff々\u30a0-\u30ffー\u3040-\u309f]{1,5}$/;
const COMPANY_RE =
  /(株式会社|有限会社|合同会社|㈱|（株）|\(株\)|（有）|\(有\)|製作所|鉄工所|工作所|精機|商会|商事|興業)/;
const CODE_TOKEN_RE = /[A-Z]{2,}[-_ ]?\d{2,}/;
// 助詞・句読点があれば「文」なので、型番が混ざっていても訳す
const PARTICLE_RE = /[のはをがにでともへや、。？！]/;
const FILENAME_RE = /\.(pdf|docx?|xlsx?|pptx?|dwg|png|jpe?g|csv|zip|txt)$/i;

/** 人名・会社名・型番・ファイル名のような業務データかどうか（形だけで判定）。 */
export function looksLikeBusinessData(text) {
  const s = String(text || '').trim();
  if (!s) return false;
  if (PERSON_NAME_RE.test(s)) return true;
  if (COMPANY_RE.test(s)) return true;
  if (FILENAME_RE.test(s)) return true;
  if (CODE_TOKEN_RE.test(s) && !PARTICLE_RE.test(s)) return true;
  return containsProtectedName(s);
}

/** True when the string must never be sent to the model. */
export function shouldSkip(text) {
  const s = String(text || '').trim();
  if (!s || s.length > MAX_ITEM_CHARS) return true;
  if (!JP_RE.test(s)) return true;              // already English / identifiers
  if (NUMERIC_RE.test(s) || PUNCT_RE.test(s)) return true;
  if (PROTECTED_RE.test(s)) return true;
  // Path-like tokens (e.g. NAS paths) keep their Japanese folder names.
  if (s.includes('/') && !/[\s、。：]/.test(s)) return true;
  if (looksLikeBusinessData(s)) return true;
  return false;
}

/**
 * T2 — モデルを呼ばずに決められる英語だけを返す（決め打ち・登録済み英語名）。
 * data-no-translate が付いた業務データでも、英語名（name_en）や定型文の形が
 * 分かっているものはこの経路で表示だけ差し替える。
 */
export function localTranslate(text) {
  const key = normaliseKey(text);
  if (!key) return null;
  return getNameEn(key) || staticTranslate(key) || null;
}

export const currentEpoch = () => epoch;
export const getCached = (text) => cache.get(normaliseKey(text)) || null;
export const getStatus = () => ({ degraded, lastErrorCategory, epoch, cached: cache.size });
/** 自動先読みが許可されているか（サーバーの containment 状態に従う）。 */
export const isPrewarmAllowed = () => prewarmAllowed;
export const getPrewarmSuspendedReason = () => prewarmSuspendedReason;
export function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }
const notify = () => listeners.forEach((fn) => { try { fn(getStatus()); } catch { /* noop */ } });

/** Called when the display language changes or the user navigates: any response
 *  belonging to an older epoch is dropped instead of touching the DOM. */
export function bumpEpoch() {
  epoch += 1;
  const dropped = queue;
  queue = [];
  if (timer) { clearTimeout(timer); timer = null; }
  dropped.forEach((item) => resolveKey(item.key, null));
  notify();
  return epoch;
}

/** Clear the failure state so the user can retry translation explicitly. */
export function retryNow() {
  negative.clear();
  rejected.clear();
  degraded = false;
  pausedUntil = 0;
  consecutiveFailures = 0;
  lastErrorCategory = null;
  notify();
}

function resolveKey(key, value) {
  const waiting = inflight.get(key);
  inflight.delete(key);
  (waiting || []).forEach((fn) => { try { fn(value); } catch { /* noop */ } });
}

/**
 * Request an English rendering of Japanese `text`.
 * Resolves with the English string, or null when it must stay Japanese.
 */
export function translate(text, contentType = 'ui_text') {
  const key = normaliseKey(text);
  if (!key) return Promise.resolve(null);
  // T3/T4 — 決め打ちで訳せる文はモデルに投げない（即時・崩れない）
  const fixed = staticTranslate(key);
  if (fixed) { cache.set(key, fixed); return Promise.resolve(fixed); }
  // T2 — 人名・会社名が入る文は訳さない（別人の名前に化けるのを防ぐ）
  // 英語名（name_en）が登録されている名前だけ、その表記で表示する。
  const en = getNameEn(key);
  if (en) { cache.set(key, en); return Promise.resolve(en); }
  if (shouldSkip(key)) return Promise.resolve(null);
  const hit = cache.get(key);
  if (hit) return Promise.resolve(hit);
  const failedAt = negative.get(key);
  if (failedAt && Date.now() - failedAt < NEGATIVE_TTL_MS) return Promise.resolve(null);
  if (Date.now() < pausedUntil) return Promise.resolve(null);

  const waiting = inflight.get(key);
  if (waiting) return new Promise((resolve) => waiting.push(resolve));

  return new Promise((resolve) => {
    inflight.set(key, [resolve]);
    queue.push({ key, text: key, contentType, epoch });
    schedule();
  });
}

function schedule() {
  if (timer) return;
  timer = setTimeout(() => { timer = null; drain(); }, DEBOUNCE_MS);
}

function drain() {
  while (queue.length && active < MAX_CONCURRENT) {
    // 対話要求（EN切替・画面遷移）を先に送り、プリウォームは後回しにする。
    const idx = queue.findIndex((q) => !q.prewarm);
    const batch = idx > 0 ? [queue.splice(idx, 1)[0]] : queue.splice(0, BATCH_SIZE);
    if (idx > 0) {
      while (batch.length < BATCH_SIZE) {
        const n = queue.findIndex((q) => !q.prewarm);
        if (n < 0) break;
        batch.push(queue.splice(n, 1)[0]);
      }
    }
    send(batch);
  }
  if (queue.length) schedule();
}

async function send(batch) {
  active += 1;
  const batchEpoch = epoch;
  const isPrewarm = batch.every((b) => b.prewarm);
  const items = batch.map((b, i) => ({
    id: `i${i}`, text: b.text, content_type: b.contentType,
  }));
  try {
    const { data } = await axios.post(`${API}/batch`, {
      source_language: 'ja', target_language: 'en',
      items, preserve_formatting: true, prewarm: isPrewarm,
    });
    if (batchEpoch !== epoch) {
      // Language changed while in flight — cache the work but never apply it.
      const late = new Map((data?.results || []).filter((r) => r && r.id)
        .map((r) => [r.id, r]));
      batch.forEach((b, i) => {
        const r = late.get(`i${i}`);
        if (r?.text) cache.set(b.key, r.text);
      });
      batch.forEach((b) => resolveKey(b.key, null));
      return;
    }
    const results = data?.results || [];
    const byId = new Map(results.filter((r) => r && r.id).map((r) => [r.id, r]));
    let ok = 0;
    batch.forEach((b, i) => {
      const r = byId.get(`i${i}`) || results[i];
      if (r && r.text) {
        cache.set(b.key, r.text);
        rejected.delete(b.key);
        ok += 1;
        resolveKey(b.key, r.text);
      } else {
        negative.set(b.key, Date.now());
        if (r && r.status === 'failed' && REJECT_REASONS.has(r.reason)) {
          rejected.add(b.key);
        }
        resolveKey(b.key, null);
      }
    });
    if (ok) persist();
    if (isPrewarm) { consecutiveFailures = 0; return; }
    if (data?.enabled === false) {
      degraded = true; lastErrorCategory = 'disabled'; notify();
    } else if (ok === 0 && results.some((r) => r?.status === 'failed')) {
      registerFailure(results.find((r) => r?.status === 'failed')?.reason || 'failed');
    } else if (ok > 0) {
      consecutiveFailures = 0;
      if (degraded) { degraded = false; lastErrorCategory = null; notify(); }
    }
  } catch (e) {
    const status = e?.response?.status;
    batch.forEach((b) => { negative.set(b.key, Date.now()); resolveKey(b.key, null); });
    if (isPrewarm) return;      // background warm-up never alarms the user
    registerFailure(status === 429 ? 'rate_limited'
      : status === 413 ? 'too_large'
        : status === 401 || status === 403 ? 'unauthorised' : 'request_failed');
  } finally {
    active -= 1;
    if (queue.length) drain();
  }
}

function registerFailure(category) {
  consecutiveFailures += 1;
  lastErrorCategory = category;
  degraded = true;
  if (consecutiveFailures >= 3) pausedUntil = Date.now() + FAILURE_PAUSE_MS;
  notify();
}

export async function fetchStatus() {
  try {
    const { data } = await axios.get(`${API}/status`);
    prewarmAllowed = data?.prewarm_enabled === true;
    prewarmSuspendedReason = data?.prewarm_suspended_reason || null;
    if (data?.cache_version && cacheVersion && data.cache_version !== cacheVersion) {
      // Prompt version or admin glossary changed — stale English is dropped.
      dropPersisted(data.cache_version);
      notify();
    } else if (data?.cache_version) {
      cacheVersion = data.cache_version;
      persist();
    }
    if (data && data.enabled === false) {
      degraded = true; lastErrorCategory = 'disabled'; notify();
    }
    return data;
  } catch {
    return null;
  }
}

/**
 * Low-priority warm-up: translate strings now so switching to EN is instant.
 * Results only land in the cache — nothing is displayed and failures are
 * silent.
 */
export function prewarm(texts) {
  // Stage 0 containment: サーバーが許可していない限り自動先読みは行わない。
  if (!prewarmAllowed) return 0;
  // 複数タブ・再読み込みでの重複プリウォームを防ぐ（30秒のリーダーロック）
  try {
    const now = Date.now();
    const last = Number(localStorage.getItem('sensbey_tr_prewarm_at') || 0);
    if (now - last < 30000) return 0;
    localStorage.setItem('sensbey_tr_prewarm_at', String(now));
  } catch { /* private mode — continue */ }
  if (document.visibilityState === 'hidden') return 0;
  const fresh = [];
  const seen = new Set();
  (texts || []).forEach((raw) => {
    const key = normaliseKey(raw);
    if (!key || seen.has(key) || shouldSkip(key)) return;
    if (cache.has(key) || inflight.has(key) || negative.has(key)) return;
    seen.add(key);
    fresh.push(key);
  });
  if (!fresh.length || Date.now() < pausedUntil) return 0;
  fresh.slice(0, 24).forEach((key) => {
    inflight.set(key, []);
    queue.push({ key, text: key, contentType: 'ui_text', epoch, prewarm: true });
  });
  schedule();
  return fresh.length;
}
